import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useCrewData } from "@/hooks/useCrewData";
import { listRoles, listTeam } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CrewCalendarView from "@/components/CrewCalendarView";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, Edit2, Trash2, CalendarDays, Settings, MessageSquare, List } from "lucide-react";
import { MultiEntryField } from "@/components/crew/MultiEntryField";
import { RoleSelector } from "@/components/crew/RoleSelector";
import { ManageRolesDialog } from "@/components/crew/ManageRolesDialog";
import { CrewCardEvents } from "@/components/crew/CrewCardEvents";
import { PortalAccess } from "@/components/crew/PortalAccess";
import { MessageTemplatesDialog } from "@/components/crew/MessageTemplatesDialog";

export default function CrewPage() {
  const { role } = useOutletContext();
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    name: "", roles: [], emails: [], phones: [],
    availability: "available", daily_rate: 0, notes: ""
  });
  const [availableRoles, setAvailableRoles] = useState([]);

  const [search, setSearch] = useState("");
  const { crew, loading, hasDateRange, fetchCrew, handleCreate, handleUpdate, handleDelete: deleteCrew } = useCrewData(dateStart, dateEnd);
  const canEdit = role === "owner" || role === "manager";
  const canDelete = role === "owner" || role === "manager";
  const isOwner = role === "owner";

  const fetchRoles = useCallback(async () => {
    try {
      const res = await listRoles();
      setAvailableRoles(res.data);
    } catch (err) { toast.error("Failed to load roles"); }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  // Portal logins (owner/manager endpoint) so each card can show link status.
  const [team, setTeam] = useState([]);
  const fetchTeam = useCallback(async () => {
    if (!canEdit) return;
    try {
      const res = await listTeam();
      setTeam(res.data);
    } catch { /* non-critical for the crew page */ }
  }, [canEdit]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const filtered = crew.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const roles = (c.roles || [c.role]).filter(Boolean);
    return c.name.toLowerCase().includes(q)
      || roles.some(r => r.toLowerCase().includes(q))
      || (c.emails || [c.email]).filter(Boolean).some(e => e.toLowerCase().includes(q));
  });

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: "", roles: [], emails: [], phones: [], availability: "available", daily_rate: 0, notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      roles: item.roles || (item.role ? [item.role] : []),
      emails: item.emails || (item.email ? [item.email] : []),
      phones: item.phones || (item.phone ? [item.phone] : []),
      availability: item.availability,
      daily_rate: item.daily_rate || 0,
      notes: item.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const cleanForm = {
      ...form,
      emails: form.emails.filter(e => e.trim()),
      phones: form.phones.filter(p => p.trim()),
    };
    try {
      if (editItem) { await handleUpdate(editItem.crew_id, cleanForm); }
      else { await handleCreate(cleanForm); }
      setDialogOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = async (id) => {
    try { await deleteCrew(id); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const initials = (name) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const avatarColors = ["bg-amber-500/20 text-amber-400", "bg-blue-500/20 text-blue-400", "bg-emerald-500/20 text-emerald-400", "bg-purple-500/20 text-purple-400", "bg-rose-500/20 text-rose-400"];

  const getRoles = (c) => c.roles?.length ? c.roles : (c.role ? [c.role] : []);
  const getEmails = (c) => c.emails?.length ? c.emails : (c.email ? [c.email] : []);
  const getPhones = (c) => c.phones?.length ? c.phones : (c.phone ? [c.phone] : []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const availableCount = crew.filter(c => c.date_availability === "available").length;
  const bookedCount = crew.filter(c => c.date_availability === "booked").length;

  return (
    <div data-testid="crew-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Crew</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {crew.length} members &middot; {availableCount} available &middot; {bookedCount} booked
            {hasDateRange && ` for ${dateStart} — ${dateEnd}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="manage-templates-btn" variant="outline" onClick={() => setTemplatesDialogOpen(true)} className="text-xs uppercase tracking-wider rounded-sm border-border">
            <MessageSquare className="w-3.5 h-3.5 mr-2" /> Message Templates
          </Button>
          <Button data-testid="manage-roles-btn" variant="outline" onClick={() => setRolesDialogOpen(true)} className="text-xs uppercase tracking-wider rounded-sm border-border">
            <Settings className="w-3.5 h-3.5 mr-2" /> Manage Roles
          </Button>
          {canEdit && (
            <Button data-testid="add-crew-btn" onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider rounded-sm">
              <Plus className="w-3.5 h-3.5 mr-2" /> Add Crew
            </Button>
          )}
        </div>
      </div>

      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="bg-secondary/30 border border-border">
          <TabsTrigger data-testid="crew-tab-list" value="list" className="text-xs uppercase tracking-wider">
            <List className="w-3 h-3 mr-1.5" /> List
          </TabsTrigger>
          <TabsTrigger data-testid="crew-tab-calendar" value="calendar" className="text-xs uppercase tracking-wider">
            <CalendarDays className="w-3 h-3 mr-1.5" /> Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-4">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Check Availability</span>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="crew-start-date" variant="outline" className="w-44 h-9 text-sm bg-secondary/30 border-border justify-start font-normal">
                        <CalendarDays className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                        {dateStart || <span className="text-muted-foreground">Pick date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateStart ? new Date(dateStart + "T00:00:00") : undefined}
                        onSelect={(day) => {
                          if (day) {
                            const d = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
                            setDateStart(d);
                            if (!dateEnd || d > dateEnd) setDateEnd(d);
                          }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="crew-end-date" variant="outline" className="w-44 h-9 text-sm bg-secondary/30 border-border justify-start font-normal">
                        <CalendarDays className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                        {dateEnd || <span className="text-muted-foreground">Pick date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateEnd ? new Date(dateEnd + "T00:00:00") : undefined}
                        onSelect={(day) => {
                          if (day) {
                            const d = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
                            setDateEnd(d);
                            if (!dateStart || d < dateStart) setDateStart(d);
                          }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {hasDateRange && (
                  <Button data-testid="crew-clear-dates" variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => { setDateStart(""); setDateEnd(""); }}>
                    Clear dates
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="crew-search" placeholder="Search crew..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-secondary/30 border-border h-9 text-sm" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">No crew found</p>}
            {filtered.map((c, idx) => {
              const displayAvail = c.date_availability || "available";
              const roles = getRoles(c);
              const emails = getEmails(c);
              const phones = getPhones(c);
              return (
                <Card key={c.crew_id} data-testid={`crew-card-${c.crew_id}`} className="border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className={avatarColors[idx % avatarColors.length]}>{initials(c.name)}</AvatarFallback>
                      </Avatar>
                      <Badge variant="outline" className={`text-xs status-${displayAvail}`}>{displayAvail}</Badge>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{c.name}</h3>
                    {roles.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {roles.map(r => (
                          <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
                        ))}
                      </div>
                    )}
                    {isOwner && c.daily_rate > 0 && (
                      <p className="text-xs text-primary font-medium mb-2">${c.daily_rate}/day</p>
                    )}
                    <div className="space-y-1">
                      {emails.map((e, i) => (
                        <div key={`email-${e}`} className="flex items-center gap-2 text-xs text-muted-foreground"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{e}</span></div>
                      ))}
                      {phones.map((p, i) => (
                        <div key={`phone-${p}`} className="flex items-center gap-2 text-xs text-muted-foreground"><Phone className="w-3 h-3 shrink-0" />{p}</div>
                      ))}
                    </div>
                    <CrewCardEvents crewId={c.crew_id} canEdit={canEdit} onUpdate={fetchCrew} />
                    {canEdit && <PortalAccess crewMember={c} team={team} onRefresh={fetchTeam} />}
                    {c.notes && <p className="text-xs text-muted-foreground mt-2 border-t border-border pt-2">{c.notes}</p>}
                    {canEdit && (
                      <div className="flex gap-1 mt-3 pt-2 border-t border-border">
                        <Button data-testid={`edit-crew-${c.crew_id}`} variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(c)}><Edit2 className="w-3 h-3 mr-1" />Edit</Button>
                        {canDelete && <Button data-testid={`delete-crew-${c.crew_id}`} variant="ghost" size="sm" className="h-7 text-xs text-red-400 hover:text-red-300" onClick={() => onDelete(c.crew_id)}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <CrewCalendarView />
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-card border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{editItem ? 'Edit Crew' : 'Add Crew'}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Crew member details</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Name</Label>
              <Input data-testid="crew-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-secondary/30 border-border" />
            </div>

            <RoleSelector
              selected={form.roles}
              onChange={roles => setForm({...form, roles})}
              availableRoles={availableRoles.map(r => r.name)}
            />

            <MultiEntryField
              label="Phone Numbers"
              entries={form.phones}
              onChange={phones => setForm({...form, phones})}
              placeholder="(555) 123-4567"
              icon={Phone}
              testIdPrefix="crew-phone"
            />

            <MultiEntryField
              label="Email Addresses"
              entries={form.emails}
              onChange={emails => setForm({...form, emails})}
              placeholder="name@example.com"
              icon={Mail}
              testIdPrefix="crew-email"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Availability</Label>
                <Select value={form.availability} onValueChange={v => setForm({...form, availability: v})}>
                  <SelectTrigger data-testid="crew-avail-select" className="bg-secondary/30 border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isOwner && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Daily Rate ($)</Label>
                  <Input data-testid="crew-rate-input" type="number" min={0} value={form.daily_rate} onChange={e => setForm({...form, daily_rate: parseFloat(e.target.value) || 0})} className="bg-secondary/30 border-border" />
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Notes</Label>
              <Input data-testid="crew-notes-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-secondary/30 border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="crew-cancel-btn" variant="secondary" onClick={() => setDialogOpen(false)} className="text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="crew-save-btn" onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider">{editItem ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageRolesDialog
        open={rolesDialogOpen}
        onOpenChange={setRolesDialogOpen}
        roles={availableRoles}
        onRefresh={fetchRoles}
      />

      <MessageTemplatesDialog
        open={templatesDialogOpen}
        onOpenChange={setTemplatesDialogOpen}
      />
    </div>
  );
}
